/**
 * Exponential backoff polling utility.
 *
 * Starts with a short interval and doubles after each poll, up to a
 * configurable maximum interval. Automatically stops after a maximum
 * total duration to prevent indefinite polling.
 *
 * Usage:
 *   const poller = new PollingTimer({ initialInterval: 5000 });
 *   poller.start(async () => { ... });
 *   poller.reset();   // reset interval back to initial (e.g. new work arrived)
 *   poller.stop();    // stop polling
 */

export interface PollingOptions {
  /** Initial interval in ms (default: 5000) */
  initialInterval?: number;
  /** Maximum interval in ms (default: 900_000 = 15 minutes) */
  maxInterval?: number;
  /** Multiplier applied after each poll (default: 2) */
  backoffMultiplier?: number;
  /** Maximum total elapsed time in ms before auto-stop (default: 900_000 = 15 minutes) */
  maxDuration?: number;
  /** Called when maxDuration is exceeded */
  onTimeout?: () => void;
}

const DEFAULTS = {
  initialInterval: 5_000,
  maxInterval: 15 * 60 * 1_000,    // 15 minutes
  backoffMultiplier: 2,
  maxDuration: 15 * 60 * 1_000,    // 15 minutes
} as const;

export class PollingTimer {
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private currentInterval: number;
  private callback: (() => void | Promise<void>) | null = null;
  private startTime: number = 0;
  private running = false;

  readonly initialInterval: number;
  readonly maxInterval: number;
  readonly backoffMultiplier: number;
  readonly maxDuration: number;
  private readonly onTimeout?: () => void;

  constructor(options?: PollingOptions) {
    this.initialInterval = options?.initialInterval ?? DEFAULTS.initialInterval;
    this.maxInterval = options?.maxInterval ?? DEFAULTS.maxInterval;
    this.backoffMultiplier = options?.backoffMultiplier ?? DEFAULTS.backoffMultiplier;
    this.maxDuration = options?.maxDuration ?? DEFAULTS.maxDuration;
    this.onTimeout = options?.onTimeout;
    this.currentInterval = this.initialInterval;
  }

  /**
   * Start polling. Executes the callback immediately, then schedules
   * subsequent calls with exponential backoff.
   */
  start(callback: () => void | Promise<void>): void {
    this.stop();
    this.callback = callback;
    this.running = true;
    this.currentInterval = this.initialInterval;
    this.startTime = Date.now();

    // Immediate first call
    callback();
    this.scheduleNext();
  }

  /** Stop polling and clear state. */
  stop(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    this.running = false;
    this.callback = null;
  }

  /**
   * Reset interval to initial and reschedule.
   * Call this when new work arrives (e.g. a new job is added) to
   * switch back to fast polling.
   */
  reset(): void {
    this.currentInterval = this.initialInterval;
    this.startTime = Date.now();

    if (this.running && this.callback) {
      if (this.timerId !== null) {
        clearTimeout(this.timerId);
      }
      this.callback();
      this.scheduleNext();
    }
  }

  /** Whether the timer is currently running. */
  get isRunning(): boolean {
    return this.running;
  }

  /** Current backoff interval in ms (useful for testing / logging). */
  getCurrentInterval(): number {
    return this.currentInterval;
  }

  /** Elapsed time since start (or last reset) in ms. */
  getElapsed(): number {
    if (!this.running) return 0;
    return Date.now() - this.startTime;
  }

  // --- private ---

  private scheduleNext(): void {
    if (!this.callback || !this.running) return;

    const cb = this.callback;

    this.timerId = setTimeout(() => {
      if (!this.running) return;

      // Check if we've exceeded the max duration
      if (Date.now() - this.startTime >= this.maxDuration) {
        this.stop();
        this.onTimeout?.();
        return;
      }

      // Fire callback (async results are not awaited — next tick is
      // scheduled immediately so the backoff cadence stays consistent)
      cb();

      // Increase interval for the next call
      this.currentInterval = Math.min(
        this.currentInterval * this.backoffMultiplier,
        this.maxInterval,
      );

      this.scheduleNext();
    }, this.currentInterval);
  }
}
